import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { downloadAttachment } from '../lib/download';
import { shortDate } from '../lib/format';
import {
  ACCEPTED_ATTACHMENT_TYPES,
  ImageUploadError,
  uploadOrderAttachment,
} from '../lib/image-upload';
import { haptic } from '../lib/telegram';
import type { OrderAttachmentKind, OrderFiles as OrderFilesData } from '../types';
import { ZoomableImage } from './zoomable-image';
import { Button, Card, ErrorNotice, Field, Select } from './ui';

/** Названия групп: словами художника, а не именами из базы. */
const KIND_LABELS: Record<OrderAttachmentKind, string> = {
  REFERENCE: 'Референсы заказчика',
  SKETCH: 'Эскизы',
  PROGRESS: 'В процессе',
  FINAL: 'Финал',
  RECEIPT: 'Чеки об оплате',
  OTHER: 'Прочее',
};

const UPLOAD_KINDS: OrderAttachmentKind[] = ['SKETCH', 'PROGRESS', 'FINAL', 'REFERENCE', 'OTHER'];

/** Документ показывать нечем: превью у PDF и ZIP нет, только имя и размер. */
export function isDocumentAttachment(mimeType: string): boolean {
  return !mimeType.startsWith('image/');
}

export function DocumentTile({ fileName, sizeBytes }: { fileName: string; sizeBytes: number }) {
  return (
    <span className="file-tile__document">
      <span className="file-tile__ext">{extensionOf(fileName)}</span>
      <span className="file-tile__name">{fileName}</span>
      <span className="meta">{humanSize(sizeBytes)}</span>
    </span>
  );
}

function extensionOf(fileName: string): string {
  const parts = fileName.split('.');

  return parts.length > 1 ? parts.pop()!.toUpperCase().slice(0, 4) : 'ФАЙЛ';
}

function humanSize(bytes: number): string {
  return bytes < 1_000_000
    ? `${Math.max(1, Math.round(bytes / 1000))} КБ`
    : `${(bytes / 1_000_000).toFixed(1)} МБ`;
}

/**
 * Все файлы заказа в одном месте. Раньше картинку можно было найти только
 * пролистав историю этапов или переписку — здесь они собраны по группам, и
 * каждая версия остаётся на месте: старый эскиз не затирается новым.
 */
export function OrderFiles({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<OrderAttachmentKind>('PROGRESS');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const files = useQuery({
    queryKey: ['order', orderId, 'files'],
    queryFn: () => api<OrderFilesData>(`/orders/${orderId}/attachments`),
  });
  const visibility = useMutation({
    mutationFn: ({ id, value }: { id: string; value: 'PUBLIC' | 'PRIVATE' }) =>
      api<OrderFilesData>(`/orders/${orderId}/attachments/${id}`, {
        method: 'PATCH',
        body: { visibility: value },
      }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['order', orderId, 'files'], updated);
    },
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['order', orderId, 'files'] });
    void queryClient.invalidateQueries({ queryKey: ['order', orderId] });
  };

  const download = async (url: string, fileName: string) => {
    setError(null);
    try {
      await downloadAttachment(url, fileName);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'Не удалось скачать файл');
    }
  };

  const upload = async (list: FileList | null) => {
    if (!list || list.length === 0) return;

    setPending(true);
    setError(null);

    try {
      // Последовательно: параллельные загрузки на мобильной сети чаще отваливаются.
      for (const file of [...list]) {
        await uploadOrderAttachment(orderId, file, 'PUBLIC', kind);
      }
      haptic('success');
      refresh();
    } catch (failure) {
      setError(
        failure instanceof ImageUploadError || failure instanceof ApiError
          ? failure.message
          : 'Не удалось загрузить файл',
      );
    } finally {
      setPending(false);
      if (input.current) input.current.value = '';
    }
  };

  const data = files.data;

  return (
    <>
      <div className="section-heading">
        <h2>Файлы</h2>
        {data && (
          <span className="meta">
            {data.total} из {data.limit}
          </span>
        )}
      </div>

      <Card className="stack stack--compact">
        {files.error && <ErrorNotice error={files.error} />}

        {data?.groups.length === 0 && (
          <p className="muted">
            Эскизы, промежуточные результаты и финал. Картинки, PDF и ZIP до 5 МБ.
          </p>
        )}

        {data?.groups.map((group) => (
          <section key={group.kind} className="file-group">
            <h3>{KIND_LABELS[group.kind]}</h3>
            <div className="file-grid">
              {group.items.map((file) => (
                <figure key={file.id} className="file-tile">
                  {file.url && !isDocumentAttachment(file.mimeType) ? (
                    <ZoomableImage
                      url={file.url}
                      alt={`${KIND_LABELS[group.kind]} v${file.version}`}
                    />
                  ) : (
                    <DocumentTile fileName={file.fileName} sizeBytes={file.sizeBytes} />
                  )}
                  <span className="file-tile__version">v{file.version}</span>
                  <figcaption>
                    <span className="meta">{shortDate(file.createdAt)}</span>
                    <div className="file-tile__actions">
                      {file.downloadUrl && (
                        <button
                          type="button"
                          onClick={() => void download(file.downloadUrl!, file.fileName)}
                        >
                          Скачать
                        </button>
                      )}
                      {/* Видимость меняется и после загрузки: ошиблись — исправили. */}
                      <button
                        type="button"
                        disabled={visibility.isPending}
                        onClick={() =>
                          visibility.mutate({
                            id: file.id,
                            value: file.visibility === 'PUBLIC' ? 'PRIVATE' : 'PUBLIC',
                          })
                        }
                      >
                        {file.visibility === 'PUBLIC' ? 'Скрыть' : 'Открыть'}
                      </button>
                    </div>
                  </figcaption>
                  {file.visibility === 'PRIVATE' && (
                    <span className="file-tile__private">только вы</span>
                  )}
                </figure>
              ))}
            </div>
          </section>
        ))}

        {visibility.error && <ErrorNotice error={visibility.error} />}

        <Field label="Куда положить">
          <Select
            value={kind}
            onChange={(event) => setKind(event.target.value as OrderAttachmentKind)}
          >
            {UPLOAD_KINDS.map((value) => (
              <option key={value} value={value}>
                {KIND_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>
        {error && <span className="field__error">{error}</span>}
        <Button
          variant="secondary"
          disabled={pending || (data ? data.total >= data.limit : false)}
          onClick={() => input.current?.click()}
        >
          {pending ? 'Загружаем…' : '+ Добавить файлы'}
        </Button>

        <input
          ref={input}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES}
          multiple
          hidden
          onChange={(event) => void upload(event.target.files)}
        />
      </Card>
    </>
  );
}
